import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, GiftIcon, ClockIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  loyaltyPointsAPI,
  LoyaltyPointsSummary,
  LoyaltyRedemption,
} from '../services/api';
import api from '../services/api';
import { salesLadderService } from '../services/salesLadderService';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Card from '../components/common/Card';
import { SkeletonLoader } from '../components/ui/SkeletonLoader';

const MIN_REDEEM_POINTS = 1000;

const formatGhs = (amount: number) =>
  `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const StatusBadge: React.FC<{ status: LoyaltyRedemption['status'] }> = ({ status }) => {
  const map = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    approved: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${map[status]}`}>
      {status === 'approved' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
      {status === 'rejected' && <XCircleIcon className="h-3 w-3 mr-1" />}
      {status === 'pending' && <ClockIcon className="h-3 w-3 mr-1" />}
      {status}
    </span>
  );
};

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Earn Points',
    desc: 'Get loyalty points automatically with every data bundle or airtime purchase you make.',
  },
  {
    step: '2',
    title: 'Accumulate 1,000+',
    desc: 'Once you hit 1,000 points, you can request a wallet credit redemption at any time.',
  },
  {
    step: '3',
    title: 'Redeem for Credit',
    desc: 'Submit your redemption request and receive wallet credit within 24 hours after approval.',
  },
];

const SalesLadderPage: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<LoyaltyPointsSummary | null>(null);
  const [redemptions, setRedemptions] = useState<LoyaltyRedemption[]>([]);
  const [myRank, setMyRank] = useState<{ position: number | null; movement: string; totalParticipants: number } | null>(null);
  const [tierName, setTierName] = useState('Bronze');
  const [tierMultiplier, setTierMultiplier] = useState(1.0);

  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, redRes, rankRes, referralRes] = await Promise.all([
        loyaltyPointsAPI.getSummary(),
        loyaltyPointsAPI.getRedemptions(20),
        salesLadderService.getMyPosition().catch(() => null),
        api.get('/referral/stats').catch(() => null),
      ]);
      setSummary(sumRes.data?.data ?? null);
      setRedemptions(Array.isArray(redRes.data?.data) ? redRes.data.data : []);
      setMyRank(rankRes?.data ?? null);
      setTierName(referralRes?.data?.data?.tier?.current ?? 'Bronze');
      setTierMultiplier(Number(referralRes?.data?.data?.tier?.bonusMultiplier ?? 1.0));
    } catch {
      toast.error('Failed to load loyalty points data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const estimatedCredit =
    summary && redeemPoints
      ? (parseInt(redeemPoints, 10) || 0) * summary.pointsToGhsRate
      : 0;

  const handleRedeem = async () => {
    setRedeemError('');
    const pts = parseInt(redeemPoints, 10);
    if (!pts || pts < MIN_REDEEM_POINTS) {
      setRedeemError(`Minimum redemption is ${MIN_REDEEM_POINTS.toLocaleString()} points`);
      return;
    }
    if (summary && pts > summary.availablePoints) {
      setRedeemError('Insufficient points for this redemption');
      return;
    }
    setRedeemLoading(true);
    try {
      await loyaltyPointsAPI.redeem(pts);
      toast.success('Redemption request submitted! Credit arrives within 24 hours.');
      setRedeemOpen(false);
      setRedeemPoints('');
      setLoading(true);
      await fetchData();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.message;
      if (code === 'INSUFFICIENT_POINTS') {
        setRedeemError('You do not have enough points for this amount');
      } else {
        setRedeemError(msg || 'Redemption failed. Please try again.');
      }
    } finally {
      setRedeemLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Loyalty Points</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Earn and redeem points for wallet credit</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto p-4 space-y-6"
      >
        {/* Summary Card */}
        <Card variant="elevated" className="border-0 overflow-hidden">
          <div style={{ background: 'linear-gradient(135deg, #0e2044, #152e5c)' }} className="p-6 rounded-xl">
            {loading ? (
              <div className="space-y-3">
                <SkeletonLoader className="h-6 w-40 bg-white/20" />
                <SkeletonLoader className="h-14 w-56 bg-white/20" />
                <SkeletonLoader className="h-4 w-32 bg-white/20" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <SparklesIcon className="h-5 w-5 text-yellow-300" />
                  <span className="text-sm font-medium text-white/80">Available Points</span>
                </div>

                <div className="flex items-end gap-4 mb-4">
                  <span className="text-5xl font-bold text-white">
                    {(summary?.availablePoints ?? 0).toLocaleString()}
                  </span>
                  <span className="text-lg text-white/70 mb-1">pts</span>
                </div>

                <p className="text-white/70 text-sm mb-1">
                  ≈ {formatGhs(summary?.estimatedWalletCredit ?? 0)} wallet credit
                </p>

                {tierName !== 'Bronze' && (
                  <div className="flex items-center gap-2 mt-2 mb-1">
                    <span className="text-xs font-semibold text-amber-300 bg-white/10 px-2.5 py-1 rounded-full">
                      {tierName} · {tierMultiplier}× points multiplier
                    </span>
                  </div>
                )}
                {tierName === 'Bronze' && (
                  <p className="text-xs text-white/50 mt-1 mb-1">
                    Refer 5 friends to unlock 1.15× bonus points
                  </p>
                )}

                {(summary?.pendingRedemptionPoints ?? 0) > 0 && (
                  <div className="inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1 mb-4 mt-2">
                    <ClockIcon className="h-3.5 w-3.5 text-yellow-300" />
                    <span className="text-xs text-white">
                      {(summary?.pendingRedemptionPoints ?? 0).toLocaleString()} pts pending (
                      {formatGhs(summary?.pendingRedemptionCredit ?? 0)})
                    </span>
                  </div>
                )}

                <div className="mt-4">
                  <Button
                    variant="secondary"
                    size="md"
                    icon={<GiftIcon className="h-4 w-4" />}
                    onClick={() => { setRedeemError(''); setRedeemPoints(''); setRedeemOpen(true); }}
                    disabled={(summary?.availablePoints ?? 0) < MIN_REDEEM_POINTS}
                    className="bg-[#22b85c] hover:bg-[#1a9a4a] text-white border-0"
                  >
                    {(summary?.availablePoints ?? 0) < MIN_REDEEM_POINTS
                      ? `Need ${MIN_REDEEM_POINTS.toLocaleString()} pts to redeem`
                      : 'Redeem Points'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Weekly rank — always shown */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: myRank?.position ? 'linear-gradient(135deg, #22b85c, #1a9a4a)' : 'linear-gradient(135deg, #6b7280, #9ca3af)' }}
              >
                <span className="text-white font-black text-lg">
                  {myRank?.position ? `#${myRank.position}` : '–'}
                </span>
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-sm">
                  {!myRank
                    ? 'Position not yet available'
                    : myRank.position
                      ? `You're #${myRank.position} this week`
                      : 'Not ranked yet this week'}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {myRank?.position
                    ? 'Resets Sunday'
                    : 'Make a purchase to appear on the ranking'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {myRank?.position && myRank.position <= 3 && (
                <span className="text-2xl">
                  {myRank.position === 1 ? '🥇' : myRank.position === 2 ? '🥈' : '🥉'}
                </span>
              )}
              {myRank?.movement === 'up' && (
                <span className="text-xs font-bold text-[#22b85c] bg-[#22b85c]/10 px-2 py-1 rounded-full">
                  ↑ Moving up
                </span>
              )}
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {HOW_IT_WORKS.map(({ step, title, desc }) => (
              <Card key={step} padding="md">
                <div className="flex flex-col gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary-600 dark:text-primary-400">{step}</span>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{desc}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Redemption History */}
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Redemption History</h2>
          {loading ? (
            <Card padding="none">
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center justify-between p-4">
                    <div className="space-y-1.5">
                      <SkeletonLoader className="h-4 w-24" />
                      <SkeletonLoader className="h-3 w-16" />
                    </div>
                    <SkeletonLoader className="h-6 w-16 rounded-full" />
                  </div>
                ))}
              </div>
            </Card>
          ) : redemptions.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-6">
                <GiftIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No redemptions yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Earn points with purchases and redeem them for wallet credit.
                </p>
              </div>
            </Card>
          ) : (
            <Card padding="none">
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Points</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Credit</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {redemptions.map(r => (
                      <tr key={r._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDate(r.createdAt)}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                          {r.points.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                          {formatGhs(r.creditAmount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StatusBadge status={r.status} />
                          {r.status === 'rejected' && r.rejectionReason && (
                            <p className="text-xs text-red-500 mt-1">{r.rejectionReason}</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile list */}
              <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
                {redemptions.map(r => (
                  <div key={r._id} className="p-4 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {r.points.toLocaleString()} pts → {formatGhs(r.creditAmount)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{formatDate(r.createdAt)}</p>
                      {r.status === 'rejected' && r.rejectionReason && (
                        <p className="text-xs text-red-500 mt-1">{r.rejectionReason}</p>
                      )}
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </motion.div>

      {/* Redeem Modal */}
      <Modal
        isOpen={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        title="Redeem Points"
        size="small"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Available</span>
            <span className="font-bold text-gray-900 dark:text-white">
              {(summary?.availablePoints ?? 0).toLocaleString()} pts
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Points to redeem
            </label>
            <input
              type="number"
              min={MIN_REDEEM_POINTS}
              max={summary?.availablePoints ?? 0}
              value={redeemPoints}
              onChange={e => { setRedeemPoints(e.target.value); setRedeemError(''); }}
              placeholder={`Min ${MIN_REDEEM_POINTS.toLocaleString()}`}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {redeemError && (
              <p className="mt-1.5 text-xs text-red-500">{redeemError}</p>
            )}
          </div>

          {estimatedCredit > 0 && (
            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-3 flex items-center justify-between">
              <span className="text-sm text-primary-700 dark:text-primary-300">You'll receive</span>
              <span className="font-bold text-primary-700 dark:text-primary-300">
                {formatGhs(estimatedCredit)}
              </span>
            </div>
          )}

          <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-3">
            <ClockIcon className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
            <span>Credit is added to your wallet within 24 hours after admin approval.</span>
          </div>

          <div className="flex gap-3 pt-1">
            <Button variant="outline" size="md" fullWidth onClick={() => setRedeemOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              fullWidth
              loading={redeemLoading}
              onClick={handleRedeem}
            >
              Confirm Redemption
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SalesLadderPage;
